import { ReactNode } from 'react'

import Tooltip from '@components/Tooltip/Tooltip'
import { Notification } from '@domain'
import { IconDefinition } from '@fortawesome/pro-light-svg-icons'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconButton, Paper, Stack, Typography } from '@mui/material'
import { getTestId } from '@utils/helpers'
import dayjs from 'dayjs'
import TimeAgo from 'react-timeago'

import { agoFormatter } from './NotificationCard.utils'

type Props = {
	icon: IconDefinition
	iconColor?: string
	notification: Notification
	variant?: 'list' | 'toast'
	onClose?: () => void
	onToggleRead?: (notification: Notification) => void
	children: ReactNode
	'data-testid'?: string
}

// Wrapper component for notification templates
export default function NotificationShell({
	icon,
	iconColor = 'primary',
	notification,
	variant = 'list',
	onClose = () => {},
	onToggleRead = () => {},
	children,
	...rest
}: Props) {
	const testId = getTestId(rest, 'notification-card')

	return (
		<Paper
			component={Stack}
			direction="row"
			spacing={1.5}
			variant="outlined"
			data-testid={testId}
			{...rest}
			sx={{ px: '1.5rem', py: '1rem', borderRadius: 2, alignItems: 'flex-start' }}
		>
			<Stack
				sx={{
					color: iconColor,
					fontSize: '1rem',
					width: 32,
					height: '1.5em',
					alignItems: 'center',
					justifyContent: 'center',
					flexShrink: 0,
				}}
			>
				<FontAwesomeIcon icon={icon} size="sm" />
			</Stack>

			<Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
				{children}
			</Stack>

			{variant === 'toast' && <ToastDismiss onClose={onClose} />}
			{variant === 'list' && <ListStatus notification={notification} onToggleRead={onToggleRead} />}
		</Paper>
	)
}

function ToastDismiss({ onClose }: { onClose: () => void }) {
	return (
		<IconButton
			size="small"
			aria-label="Dismiss"
			onClick={onClose}
			data-testid="notification-card-close"
		>
			<FontAwesomeIcon icon={faXmark} size="sm" />
		</IconButton>
	)
}

function ListStatus({
	notification,
	onToggleRead,
}: {
	notification: Notification
	onToggleRead: (notification: Notification) => void
}) {
	const readLabel = notification.read ? 'Mark as unread' : 'Mark as read'
	const dotColor = notification.read ? 'success.main' : 'secondary.main'

	return (
		<Stack spacing={1} alignItems="flex-end" sx={{ flexShrink: 0, alignSelf: 'center' }}>
			<IconButton
				aria-label={readLabel}
				onClick={() => onToggleRead(notification)}
				data-testid="notification-card-dot"
				sx={{
					width: 24,
					height: 24,
					p: '8px',
					borderRadius: '50%',
					bgcolor: dotColor,
					backgroundClip: 'content-box',
					'&:hover': {
						bgcolor: dotColor,
					},
				}}
			/>

			{notification.createdAt && (
				<Tooltip title={dayjs(notification.createdAt).format('lll')}>
					<Typography variant="caption" color="text.secondary" data-testid="notification-card-time">
						<TimeAgo date={notification.createdAt} formatter={agoFormatter} />
					</Typography>
				</Tooltip>
			)}
		</Stack>
	)
}
